import {
  IBasePickerSuggestionsProps,
  initializeIcons,
  IPersonaProps,
  NormalPeoplePicker,
} from "@fluentui/react";
import React, { useContext, useState } from "react";
import { getApiUrl } from "../../api-clients";
import { StudentApi, StudentFilterDto } from "../../api-clients/api";
import { SchoolContext } from "../../contexts/SchoolContext";
import DeleteButtonCallout from "./DeleteButtonCallout";
import { useTranslation } from "react-i18next";

const { v4: uuidv4 } = require("uuid");

initializeIcons();

interface IProps {
  onStudentSelected: Function;
  onListChanged: (students: IPersonaProps[]) => void;
  selectedStudents: IPersonaProps[];
  onRemoveStudent: (studentId: string) => void;
  title?: string;
  allAccademicYears: boolean;
  maxSelect?: number;
}
export default function StudentIdPicker({
  onStudentSelected,
  onListChanged,
  selectedStudents,
  onRemoveStudent,
  title,
  allAccademicYears,
  maxSelect,
}: IProps) {
  const { t } = useTranslation();
  const [uniqueId] = useState<string>(uuidv4());
  const { accademicYear } = useContext(SchoolContext);
  const [studentToRemove, setStudentToRemove] =
    useState<IPersonaProps | null>(null);
  const [studentApi] = useState<StudentApi>(new StudentApi(getApiUrl()));
  const [filter] = useState<StudentFilterDto>({
    accademicYearId: allAccademicYears ? null : accademicYear?.accademicYearId,
    // ignoreAccademicYearIds: !allAccademicYears ? null : [accademicYear?.accademicYearId],
    pageSize: 20,
  } as StudentFilterDto);

  const suggestionProps: IBasePickerSuggestionsProps = {
    suggestionsHeaderText: t("shared.suggestedStudents"),
    mostRecentlyUsedHeaderText: t("shared.suggestedStudents"),
    noResultsFoundText: t("shared.noResults"),
    loadingText: t("shared.loading"),
    showRemoveButtons: false,
    suggestionsAvailableAlertText: t("shared.studentSuggestionsAvailable"),
    suggestionsContainerAriaLabel: t("shared.suggestedStudents"),
  };

  const onFilterChanged = (
    input: string,
    selectedItems?: IPersonaProps[] | undefined
  ): IPersonaProps[] | PromiseLike<IPersonaProps[]> => {
    return studentApi
      .filter({
        ...filter,
        filter: input,
        ignoreIds: selectedItems?.map((s) => s.id),
      } as StudentFilterDto)
      .then((res) => {
        if (res && res.data && res.data.length > 0) {
          return res.data.map((s) => {
            return {
              id: s.studentId,
              text: `${s.firstName} ${s.lastName}`,
              secondaryText: `${s.classroomAcronym} - ${s.accademicYearStartDate?.getFullYear()}/${s.accademicYearEndDate?.getFullYear()}`,
              tertiaryText: s.accademicYearId
            } as IPersonaProps;
          });
        } else return [];
      });
  };

  const onItemSelected = (
    selectedItem?: IPersonaProps
  ): IPersonaProps | PromiseLike<IPersonaProps> | null => {
    if (selectedItem) onStudentSelected(selectedItem);
    return null;
  };

  return (
    <div>
      <div>
        {title ?? t("students.title")}
        <NormalPeoplePicker
          className={uniqueId}
          onResolveSuggestions={(
            input: string,
            selectedItems?: IPersonaProps[] | undefined
          ): IPersonaProps[] | PromiseLike<IPersonaProps[]> => {
            if (maxSelect == null || selectedStudents.length < maxSelect)
              return onFilterChanged(input, selectedItems);

            return [];
          }}
          resolveDelay={300}
          pickerSuggestionsProps={suggestionProps}
          selectedItems={selectedStudents}
          onItemSelected={onItemSelected}
          disabled={studentToRemove != null}
          onChange={(items) => {
            onListChanged(items ?? []);
          }}
        />
      </div>
      <DeleteButtonCallout
        uniqueId={uniqueId}
        isCalloutVisible={studentToRemove != null}
        setIsCalloutVisible={(isVisible) => {
          if (!isVisible) setStudentToRemove(null);
        }}
        confirmAction={() => {
          onRemoveStudent(studentToRemove?.id ?? "");
          setStudentToRemove(null);
        }}
        title={t("shared.removeFromList")}
        content={t("shared.removeFromListContent")}
        titleRemoveButton={t("shared.remove")}
      />
    </div>
  );
}

StudentIdPicker.defaultProps = {
  allAccademicYears: false,
};
